// Parse CEFR markdown documents into structured chapter objects for book assembly.
// Books are structured around 25 topic chapters (from §9), with §1-8 functional
// language and §10 grammar distributed evenly across them — mirroring the email campaigns.

export interface BookChapter {
  id: string;
  number: number;
  title: string;
  content: string;
  subsections: BookSubsection[];
}

export interface BookSubsection {
  id: string;
  title: string;
  content: string;
  hasMiniDialog: boolean;
}

export interface BookMetadata {
  languageCode: string;
  languageName: string;
  level: string;
  levelName: string;
  script?: string;
  romanization?: string;
  headerSummary: string;
}

export interface ParsedBook {
  metadata: BookMetadata;
  preamble: string;
  chapters: BookChapter[];
}

export const LEVEL_NAMES: Record<string, string> = {
  A0: 'Phonics',
  A1: 'Beginner',
  A2: 'Elementary',
  B1: 'Intermediate',
  B2: 'Upper Intermediate',
  C1: 'Advanced',
  C2: 'Mastery',
};

export const LEVEL_PRICES: Record<string, number> = {
  A0: 2, A1: 3, A2: 3, B1: 4, B2: 4, C1: 5, C2: 5,
};

export const BOOK_LEVELS = ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

// Fallback topic rosters per level — the parser prefers headings from the actual document
const TOPIC_ROSTERS: Record<string, string[]> = {
  A1: [
    'Greetings & Introductions', 'Numbers, Times & Dates', 'Family & People',
    'Shopping & Transactions', 'Food & Drink', 'Shopping & Money', 'Transportation',
    'House & Home', 'Body & Health', 'Weather & Nature', 'Colors & Descriptions',
    'Clothing', 'School & Education', 'Work & Office', 'Directions & Places',
    'Hobbies & Free Time', 'Emotions & States', 'Common Verbs',
    'Classifiers / Function Words', 'Common Adjectives', 'Question Words',
    'Connectors & Particles', 'At a Restaurant', 'At a Market/Shop',
    'Emergencies & Help',
  ],
  A2: [
    'Daily Routine & Schedules', 'Phone Calls & Text Messages',
    'Friendships & Social Life', 'Cooking & Kitchen Activities',
    'Hygiene & Personal Care', 'Banking & Digital Payments',
    'Commuting & Travel Tickets', 'Household Chores & Repairs',
    'Pharmacy & Minor Ailments', 'Parks & Outdoor Recreation',
    'Personality & Character Traits', 'Fashion & Accessories',
    'Exams, Grades & Study Habits', 'Teamwork & Office Tasks',
    'Neighborhood & Local Services', 'Sports & Fitness',
    'Invitations & Social Events', 'Internet & Social Media',
    'Pets & Animal Care', 'Hotels & Accommodation',
    'Shipping & Deliveries', 'Appointments & Waiting Rooms',
    'Cafés & Street Food', 'E-Commerce & Online Orders',
    'Road Safety & Traffic Rules',
  ],
  B1: [
    'Life Goals & Ambitions', 'Romantic Relationships & Dating',
    'Travel Planning & Sightseeing', 'Movies, TV & Streaming',
    'Music Genres & Live Events', 'Books, Magazines & Reading',
    'Job Search & Interviews', 'Renting & Moving House',
    'Nutrition & Healthy Eating', 'Mental Health & Stress',
    'Cultural Traditions & Festivals', 'Home Improvement & DIY',
    'University Life & Studies', 'Volunteering & Community Work',
    'Driving & Vehicle Ownership', 'Art, Craft & Creative Hobbies',
    'Childhood Memories & Nostalgia', 'Regional Cuisine & Food Culture',
    'Personal Finance & Budgeting', 'Gym & Training Routines',
    'Recycling & Eco-Friendly Living', 'Gadgets, Apps & Technology',
    'Weddings & Life Milestones', 'Interior Design & Home Comfort',
    'Dreams, Beliefs & Superstitions',
  ],
  B2: [
    'Government & Political Systems', 'Economics & Market Forces',
    'Climate Change & Environmental Policy', 'News Media & Press Freedom',
    'Criminal Justice & Law Enforcement', 'Public Health & Hospital Systems',
    'Immigration & Cultural Integration', 'Urban Development & Housing Crisis',
    'Social Inequality & Poverty', 'Global Trade & Supply Chains',
    'Education Policy & Reform', 'Cybercrime & Digital Security',
    'Advertising & Consumer Culture', 'International Organizations & Treaties',
    'Human Rights & Social Movements', 'Scientific Discovery & Innovation',
    'Historical Events & Turning Points', 'Gender Equality & Social Roles',
    'Data Privacy & Surveillance', 'Agriculture & Food Production',
    'Energy Sources & Sustainability', 'Mass Tourism & Its Effects',
    'Nonprofits & Humanitarian Aid', 'Epidemics & Global Health Crises',
    'Demographics & Population Trends',
  ],
  C1: [
    'Academic Writing & Research Methods', 'Corporate Strategy & Leadership',
    'Ethics & Moral Philosophy', 'Psychology & Behavioral Science',
    'Linguistics & Language Acquisition', 'Architecture & Spatial Design',
    'Medical Specialties & Diagnostics', 'Engineering & Industrial Systems',
    'Legal Proceedings & Case Law', 'Investment & Financial Instruments',
    'Art History & Curation', 'Theater, Dance & Performance',
    'Diplomatic Protocol & Negotiation', 'Investigative Journalism & Ethics',
    'Conservation Biology & Ecosystems', 'Artificial Intelligence & Automation',
    'Anthropology & Ethnography', 'Music Theory & Composition',
    'Macroeconomics & Fiscal Policy', 'Defense & Strategic Studies',
    'Archaeology & Cultural Heritage', 'Astrophysics & Space Exploration',
    'Marine Science & Oceanography', 'Pharmaceutical Research & Trials',
    'Intellectual Property & Patent Law',
  ],
  C2: [
    'Literary Analysis & Critical Theory', 'Rhetoric, Persuasion & Propaganda',
    'Translation Theory & Interpretation', 'Dialectology & Sociolinguistics',
    'Classical Literature & Epic Poetry', 'Geopolitical Strategy & Statecraft',
    'Theology & Comparative Religion', 'Theoretical Physics & Cosmology',
    'Bioethics & Medical Ethics', 'Constitutional Law & Sovereignty',
    'Film Theory & Cinematic Language', 'Etymology & Historical Linguistics',
    'Political Ideologies & Movements', 'Neuroscience & Consciousness Studies',
    'Opera, Ballet & Classical Arts', 'Satire, Irony & Humor',
    'Mythology, Folklore & Oral Tradition', 'Cryptography & Information Security',
    'Evolutionary Biology & Genetics', 'Epistemology & Philosophy of Knowledge',
    'Nuclear Science & Energy Policy', 'Paleography & Script History',
    'Proverbs, Idioms & Fixed Expressions', 'Oratory & Speechmaking',
    'Taboo Language, Slang & Register Play',
  ],
};

function extractHeaderInfo(md: string): { summary: string; script?: string; romanization?: string } {
  const lines = md.split('\n');
  let summary = '';
  let script: string | undefined;
  let romanization: string | undefined;

  for (const line of lines.slice(0, 30)) {
    const trimmed = line.trim();
    if (/^##\s/.test(trimmed)) break;
    if (/script/i.test(trimmed) && /:/i.test(trimmed)) {
      script = trimmed.replace(/^[*_-]+/, '').replace(/[*_]+$/, '').trim();
    }
    if (/roman/i.test(trimmed) && /:/i.test(trimmed)) {
      romanization = trimmed.replace(/^[*_-]+/, '').replace(/[*_]+$/, '').trim();
    }
    if (trimmed && !trimmed.startsWith('#') && !trimmed.startsWith('|') && !trimmed.startsWith('---')) {
      if (!summary) summary = trimmed;
    }
  }

  return { summary, script, romanization };
}

function splitH2Sections(md: string): { preamble: string; sections: Map<number, string> } {
  const lines = md.split('\n');
  const sections = new Map<number, string>();
  let preambleLines: string[] = [];
  let currentNum = -1;
  let currentLines: string[] = [];
  let inPreamble = true;

  for (const line of lines) {
    const m = line.match(/^## (?:§\s*)?(\d+)[\s.:,—-]+/);
    if (m) {
      if (inPreamble) {
        preambleLines = [...currentLines];
        inPreamble = false;
      } else if (currentNum >= 0) {
        sections.set(currentNum, currentLines.join('\n'));
      }
      currentNum = parseInt(m[1]!, 10);
      currentLines = [line];
      continue;
    }

    const h2Generic = line.match(/^## (.+)/);
    if (h2Generic && inPreamble) {
      preambleLines = [...currentLines];
      inPreamble = false;
      const numMatch = h2Generic[1]!.match(/^(\d+)/);
      currentNum = numMatch ? parseInt(numMatch[1]!, 10) : -1;
      currentLines = [line];
      continue;
    }

    currentLines.push(line);
  }

  if (inPreamble) {
    preambleLines = currentLines;
  } else if (currentNum >= 0) {
    sections.set(currentNum, currentLines.join('\n'));
  }

  return { preamble: preambleLines.join('\n').trim(), sections };
}

function splitH3Subs(text: string): { heading: string; content: string }[] {
  const subs: { heading: string; content: string }[] = [];
  const lines = text.split('\n');
  let cur: { heading: string; lines: string[] } | null = null;

  for (const line of lines) {
    if (/^### /.test(line)) {
      if (cur) subs.push({ heading: cur.heading, content: cur.lines.join('\n') });
      cur = { heading: line, lines: [line] };
    } else if (cur) {
      cur.lines.push(line);
    }
  }
  if (cur) subs.push({ heading: cur.heading, content: cur.lines.join('\n') });
  return subs;
}

function get9Sub(md: string, n: number): string {
  const re = new RegExp(`^### (?:§\\s*)?9\\.${n}[.,:;—\\s]`);
  const nextRe = /^### (?:§\s*)?9\.\d|^## /;
  const lines = md.split('\n');
  let start = -1;
  for (let i = 0; i < lines.length; i++) {
    if (start < 0 && re.test(lines[i]!)) { start = i; continue; }
    if (start >= 0 && nextRe.test(lines[i]!) && !re.test(lines[i]!)) {
      return lines.slice(start, i).join('\n').trim();
    }
  }
  if (start >= 0) return lines.slice(start).join('\n').trim();
  return '';
}

function get9Heading(md: string, n: number): string {
  const re = new RegExp(`^### (?:§\\s*)?9\\.${n}[.,:;—\\s]+(.+)`);
  for (const line of md.split('\n')) {
    const m = line.match(re);
    if (m) return m[1]!.trim();
  }
  return '';
}

function extractGrammarPatterns(text: string): string[] {
  const lines = text.split('\n');
  const subsections: { start: number; end: number }[] = [];
  for (let i = 0; i < lines.length; i++) {
    if (/^### /.test(lines[i]!)) {
      if (subsections.length > 0) subsections[subsections.length - 1]!.end = i;
      subsections.push({ start: i, end: lines.length });
    }
  }

  const patterns: string[] = [];
  for (const sub of subsections) {
    const subLines = lines.slice(sub.start, sub.end);
    const hasH4 = subLines.some(l => /^#### /.test(l));
    if (hasH4) {
      let h4Start = -1;
      for (let j = 0; j < subLines.length; j++) {
        if (/^#### /.test(subLines[j]!)) {
          if (h4Start >= 0) patterns.push(subLines.slice(h4Start, j).join('\n').trim());
          h4Start = j;
        }
      }
      if (h4Start >= 0) patterns.push(subLines.slice(h4Start).join('\n').trim());
    } else {
      const block = subLines.join('\n').trim();
      if (block) patterns.push(block);
    }
  }
  return patterns.filter(p => p.length > 0);
}

function distribute(items: string[], buckets: number): string[] {
  const result: string[] = [];
  const perBucket = Math.ceil(items.length / buckets);
  for (let b = 0; b < buckets; b++) {
    const start = b * perBucket;
    const end = Math.min(start + perBucket, items.length);
    if (start < items.length) {
      result.push(items.slice(start, end).join('\n\n'));
    } else {
      result.push('');
    }
  }
  return result;
}

function stripSectionNumbers(text: string): string {
  return text.replace(/^(#{1,4}) (?:§\s*)?\d+(?:\.\d+)*[.,:;—]?\s+/gm, '$1 ');
}

// Lift every "#### Reading"/"Passage"/… block out of a §9.X chapter body. Returns the non-reading
// content (vocabulary, situation practice, mini-dialog) and the reading passages, so each renders in
// the right subsection. The redundant bare "#### Reading" header is dropped (the "Reading" subsection
// title already labels it) and a titled "#### Reading: <title>" is reduced to "#### <title>" — so a
// "Reading" title never repeats directly under the "Reading" heading.
const READING_H4 = /^####\s*(?:Reading|Passage|Text|Extended Reading)\b\s*[:：]?\s*(.*)$/i;
function partitionReading(sec9: string): { vocab: string; reading: string } {
  const lines = sec9.split(/\r?\n/);   // tolerate CRLF — a trailing \r breaks the header regex's $ anchor
  const vocab: string[] = [];
  const reading: string[] = [];
  let target: string[] = vocab;                 // content before the first #### belongs to vocabulary
  for (const line of lines) {
    if (/^####\s/.test(line)) {
      const rm = line.match(READING_H4);
      if (rm) {
        target = reading;
        const title = rm[1]!.trim();
        if (title) reading.push('#### ' + title);  // titled passage → keep the title, drop the "Reading:" prefix
        // bare "#### Reading" → drop the header line entirely (its body still follows)
        continue;
      }
      target = vocab;
    }
    target.push(line);
  }
  return { vocab: vocab.join('\n').trim(), reading: reading.join('\n').trim() };
}

// Convert a §7 Cultural-Knowledge "### 7.X Title" subsection into a "#### Title" reading passage.
function cultureToReading(sub: string): string {
  return sub.replace(/^###\s+(?:§\s*)?\d+(?:\.\d+)*[.,:;—]?\s+/m, '#### ').trim();
}

// A0 pronunciation books chapter on every `## ` heading. Unlike A1–C2 (numbered
// §1–§10), A0 docs use descriptive headings like "## Priority 1: …", "## Neutral
// Tone …", so we split on any level-2 heading rather than numbered sections.
function parseA0Book(
  md: string,
  languageCode: string,
  languageName: string,
): ParsedBook {
  const headerInfo = extractHeaderInfo(md);
  const lines = md.split('\n');
  const preambleLines: string[] = [];
  const rawSecs: { title: string; lines: string[] }[] = [];
  let cur: { title: string; lines: string[] } | null = null;

  for (const line of lines) {
    const m = line.match(/^##\s+(.+?)\s*$/);
    if (m) {
      cur = { title: m[1]!.replace(/^(?:§\s*)?\d+[.,:;—\s]+/, '').trim(), lines: [] };
      rawSecs.push(cur);
    } else if (cur) {
      cur.lines.push(line);
    } else {
      preambleLines.push(line);
    }
  }

  const chapters: BookChapter[] = rawSecs.map((s, i) => {
    const body = s.lines.join('\n');
    const subs = splitH3Subs(body);
    const subsections: BookSubsection[] = subs.map((ss, j) => ({
      id: `sub-${i}-${j}`,
      title: ss.heading.replace(/^### /, '').trim(),
      content: ss.content,
      hasMiniDialog: /^>\s/m.test(ss.content) || ss.content.includes('Mini-dialog'),
    }));
    return {
      id: `ch-${i + 1}`,
      number: i + 1,
      title: s.title || `Section ${i + 1}`,
      content: body,
      subsections,
    };
  });

  const preamble = preambleLines.join('\n').trim();

  return {
    metadata: {
      languageCode,
      languageName,
      level: 'A0',
      levelName: 'Phonics',
      script: headerInfo.script,
      romanization: headerInfo.romanization,
      headerSummary: headerInfo.summary,
    },
    preamble,
    chapters,
  };
}

// A1-C2: 25 topic-driven chapters mirroring the email campaign structure
function parseTopicBook(
  md: string,
  languageCode: string,
  languageName: string,
  level: string,
): ParsedBook {
  const headerInfo = extractHeaderInfo(md);
  const { preamble, sections } = splitH2Sections(md);
  const fallbackTopics = TOPIC_ROSTERS[level] || [];
  const hasReadings = ['B2', 'C1', 'C2'].includes(level);

  // §9 text — this drives the 25 chapters
  const sec9Text = sections.get(9) || '';

  // §1-6 functional language — collect all ### subsections, distribute across 25.
  // (§7 CULTURAL KNOWLEDGE is NOT functional language — it is reading material, handled below.)
  const funcSubs: string[] = [];
  for (let i = 1; i <= 6; i++) {
    const secText = sections.get(i) || '';
    const subs = splitH3Subs(secText);
    funcSubs.push(...subs.map(s => s.content));
  }
  const funcDistributed = distribute(funcSubs, 25);

  // §7 CULTURAL KNOWLEDGE — encyclopedic passages (regions, festivals, economy…). They are readings,
  // so they are distributed across the 25 chapters and appended to each chapter's Reading section as
  // additional readings, NOT shown as "Functional Language".
  const cultureSubs = hasReadings
    ? splitH3Subs(sections.get(7) || '').map(s => cultureToReading(s.content)).filter(s => s.trim())
    : [];
  const cultureDistributed = distribute(cultureSubs, 25);

  // §8 (Exercise Type Mapping) is intentionally EXCLUDED from the final books
  // (Michael's call after reviewing A1) — it is not distributed into any chapter.

  // §10 grammar patterns — distribute across 25
  const sec10Text = sections.get(10) || '';
  const grammarPatterns = extractGrammarPatterns(sec10Text);
  const gramDistributed = distribute(grammarPatterns, 25);

  const chapters: BookChapter[] = [];

  for (let ch = 1; ch <= 25; ch++) {
    const docHeading = get9Heading(sec9Text, ch);
    const topicTitle = docHeading || fallbackTopics[ch - 1] || `Topic ${ch}`;
    const sec9Content = get9Sub(sec9Text, ch);

    const subsections: BookSubsection[] = [];
    let fullContent = '';

    // The §9.X block embeds its reading passages under "#### Reading"/"Reading: <title>" headers.
    // Split them out so the vocabulary/situations render in one subsection and the readings in the
    // "Reading" subsection — never both (which duplicated the reading) and never a repeated title.
    let vocabContent = sec9Content;
    let readingBlock = '';
    if (hasReadings && sec9Content) {
      const p = partitionReading(sec9Content);
      vocabContent = p.vocab;
      readingBlock = p.reading;
    }

    // 1. Vocabulary & Situations (from §9.X, with the reading passages removed)
    if (vocabContent) {
      const cleaned = stripSectionNumbers(vocabContent);
      subsections.push({
        id: `ch${ch}-vocab`,
        title: 'Vocabulary & Situations',
        content: cleaned,
        hasMiniDialog: /^>\s/m.test(cleaned) || cleaned.includes('Mini-dialog'),
      });
      fullContent += cleaned + '\n\n';
    }

    // 2. Reading (B2-C2 only) — the §9.X reading passages plus any §7 cultural passage for this chapter
    const cultureBlock = cultureDistributed[ch - 1] || '';
    const readingCombined = [readingBlock, cultureBlock].filter(s => s.trim()).join('\n\n').trim();
    if (readingCombined) {
      subsections.push({
        id: `ch${ch}-reading`,
        title: 'Reading',
        content: readingCombined,
        hasMiniDialog: false,
      });
      fullContent += readingCombined + '\n\n';
    }

    // 3. Functional Language (from §1-6, evenly distributed; §7 is reading, §8 excluded)
    const funcBlock = funcDistributed[ch - 1] || '';
    if (funcBlock.trim()) {
      const cleaned = stripSectionNumbers(funcBlock.trim());
      subsections.push({
        id: `ch${ch}-func`,
        title: 'Functional Language',
        content: cleaned,
        hasMiniDialog: /^>\s/m.test(cleaned),
      });
      fullContent += cleaned + '\n\n';
    }

    // 4. Grammar Patterns (from §10, evenly distributed)
    const gramBlock = gramDistributed[ch - 1] || '';
    if (gramBlock.trim()) {
      const cleaned = stripSectionNumbers(gramBlock.trim());
      subsections.push({
        id: `ch${ch}-gram`,
        title: 'Grammar Patterns',
        content: cleaned,
        hasMiniDialog: false,
      });
      fullContent += cleaned + '\n\n';
    }

    chapters.push({
      id: `ch-${ch}`,
      number: ch,
      title: topicTitle,
      content: fullContent.trim(),
      subsections,
    });
  }

  return {
    metadata: {
      languageCode,
      languageName,
      level,
      levelName: LEVEL_NAMES[level] ?? level,
      script: headerInfo.script,
      romanization: headerInfo.romanization,
      headerSummary: headerInfo.summary,
    },
    preamble,
    chapters,
  };
}

export function parseCefrToBook(
  md: string,
  languageCode: string,
  languageName: string,
  level: string,
): ParsedBook {
  if (level === 'A0') {
    return parseA0Book(md, languageCode, languageName);
  }
  return parseTopicBook(md, languageCode, languageName, level);
}

export function bookTitle(languageName: string, levelName: string): string {
  return `Glossika ${languageName}`;
}

export function bookSubtitle(levelName: string): string {
  return levelName;
}
